import axios from 'axios';
import moment from 'moment';

// Fyers API v3 base URLs (data calls go direct with the access token)
const FYERS_API_URL = 'https://api-t1.fyers.in/api/v3';
const FYERS_DATA_URL = 'https://api-t1.fyers.in/data';
const FYERS_SYMBOL_MASTER_URL = 'https://public.fyers.in/sym_details';
const FYERS_WS_URL = 'wss://api.fyers.in/socket/v2/data/';

// Server endpoints (credentials stay server-side)
const API_BASE = process.env.VUE_APP_API_BASE || '';

// Timeframe mapping: app timeframes → Fyers resolution values
// Per Fyers API docs:
// Seconds: 5S, 10S, 15S, 30S, 45S
// Minutes: 1, 2, 3, 5, 10, 15, 20, 30, 60, 120, 240
// Day: D, 1D
// Week: 1W
// Month: 1M
const TIMEFRAME_MAP = {
    // Seconds
    '5s': '5S',
    '10s': '10S',
    '15s': '15S',
    '30s': '30S',
    '45s': '45S',
    // Minutes
    '1m': '1',
    '2m': '2',
    '3m': '3',
    '5m': '5',
    '10m': '10',
    '15m': '15',
    '20m': '20',
    '30m': '30',
    '1H': '60',
    '2H': '120',
    '4H': '240',
    // Day/Week/Month
    '1D': 'D',
    '1W': 'D',  // Use daily and aggregate to weekly
    '1M': 'D'   // Use daily and aggregate to monthly
};

// Date range limits per Fyers API documentation
// Intraday resolutions (1, 2, 3, 5, 10, 15, 20, 30, 45, 60, 120, 180, 240): Maximum 100 days per request
// Daily (D) resolution: Maximum 366 days per request
const DATE_RANGE_LIMITS = {
    // Intraday resolutions (in minutes)
    '1': 100,    // 1 minute
    '2': 100,    // 2 minutes
    '3': 100,    // 3 minutes
    '5': 100,    // 5 minutes
    '10': 100,   // 10 minutes
    '15': 100,   // 15 minutes
    '20': 100,   // 20 minutes
    '30': 100,   // 30 minutes
    '60': 100,   // 60 minutes (1 hour)
    '120': 100,  // 120 minutes (2 hours)
    '180': 100,  // 180 minutes (3 hours)
    '240': 100,  // 240 minutes (4 hours)
    // Second-based resolutions (also intraday)
    '5S': 100,   // 5 seconds
    '10S': 100,  // 10 seconds
    '15S': 100,  // 15 seconds
    '30S': 100,  // 30 seconds
    '45S': 100,  // 45 seconds
    // Daily resolution
    'D': 366     // Daily - maximum 366 days
};

// Supported timeframes list for UI filtering (in display order)
// Seconds are separated for dropdown loading
export const FYERS_SUPPORTED_TFS = ['1m', '2m', '3m', '5m', '10m', '15m', '20m', '30m', '1H', '2H', '4H', '1D', '1W', '1M'];
export const FYERS_SECONDS_TFS = ['5s', '10s', '15s', '30s', '45s'];

export default {
    accessToken: null,
    appId: null,
    _pendingState: null,
    wsConnection: null,
    symbols: [],
    reconnectAttempts: 0,
    _wsOnMessage: null,
    _wsOnError: null,
    _wsSymbols: null,

    // ─── Authentication (server-side credentials) ───

    /**
     * Get the Fyers OAuth login URL from the server.
     * The server builds the URL with redirect_uri pointing to /api/fyers/callback.
     */
    async getAuthUrl() {
        const token = await this._getClerkToken();
        const response = await axios.get(API_BASE + '/api/fyers/auth-url', {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (response.data && response.data.success) {
            this._pendingState = response.data.state;
            return response.data.authUrl;
        }
        throw new Error(response.data?.error || 'Failed to get Fyers auth URL');
    },

    /**
     * Complete the OAuth flow after the user finishes login in the popup.
     * Sends the state to the server, which looks up the auth code received
     * at /api/fyers/callback, exchanges it for an access_token, and stores
     * the token in Clerk metadata for future sessions.
     */
    async completeLogin(state) {
        const stateToUse = state || this._pendingState;
        if (!stateToUse) {
            throw new Error('No pending Fyers auth state');
        }

        const token = await this._getClerkToken();
        const response = await axios.post(API_BASE + '/api/fyers/complete',
            { state: stateToUse },
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );

        if (response.data && response.data.success) {
            this.accessToken = response.data.accessToken;
            this.appId = response.data.appId;

            localStorage.setItem('fyers_access_token', this.accessToken);
            localStorage.setItem('fyers_app_id', this.appId);

            this._pendingState = null;
            return { success: true, accessToken: this.accessToken };
        }

        throw new Error(response.data.error || 'Failed to complete Fyers login');
    },

    /**
     * Restore Fyers session from Clerk user metadata via the server.
     * Called on page load to check if user has a saved Fyers connection.
     */
    async restoreSessionFromServer() {
        try {
            const token = await this._getClerkToken();
            if (!token) return false;

            const response = await axios.get(API_BASE + '/api/fyers/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.accessToken) {
                this.accessToken = response.data.accessToken;
                this.appId = response.data.appId;

                localStorage.setItem('fyers_access_token', this.accessToken);
                localStorage.setItem('fyers_app_id', this.appId);

                return true;
            }
        } catch (error) {
            console.warn('Could not restore Fyers session from server:', error.message);
        }
        return false;
    },

    /**
     * Validate the current Fyers session by calling the profile endpoint.
     * Returns: { valid: true } if token works, { valid: false, expired: true } if expired.
     */
    async validateSession() {
        if (!this.isAuthenticated()) {
            return { valid: false, expired: false, neverConnected: true };
        }
        try {
            const token = await this._getClerkToken();
            if (!token) return { valid: false, expired: false };

            // Use the server-proxied history endpoint with a minimal request to test auth
            const response = await axios.get(API_BASE + '/api/fyers/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.accessToken) {
                // Token exists on server, now verify it actually works with Fyers
                try {
                    await this.getProfile();
                    return { valid: true, expired: false };
                } catch (profileErr) {
                    const status = profileErr.response && profileErr.response.status;
                    if (status === 401 || status === 403) {
                        return { valid: false, expired: true };
                    }
                    // Network error or other issue — assume valid to avoid false positives
                    return { valid: true, expired: false };
                }
            }
            return { valid: false, expired: false, neverConnected: true };
        } catch (error) {
            console.warn('[Fyers] validateSession error:', error.message);
            return { valid: false, expired: false };
        }
    },

    /**
     * Check if user has ever connected Fyers (has metadata on server).
     * Different from isAuthenticated() which only checks localStorage.
     */
    async hasEverConnected() {
        try {
            const token = await this._getClerkToken();
            if (!token) return false;

            const response = await axios.get(API_BASE + '/api/fyers/session', {
                headers: { Authorization: `Bearer ${token}` }
            });
            // If server has a record (even if expired), user has connected before
            return !!(response.data && response.data.connected);
        } catch (error) {
            return false;
        }
    },

    /**
     * Disconnect Fyers — clear local + Clerk metadata.
     */
    async disconnectFromServer() {
        try {
            const token = await this._getClerkToken();
            if (token) {
                await axios.post(API_BASE + '/api/fyers/disconnect', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.warn('Could not clear Fyers session on server:', error.message);
        }
        this.clearSession();
    },

    /**
     * Get the Clerk JWT token for authenticated API calls.
     */
    async _getClerkToken() {
        try {
            if (window.__vue_clerk && window.__vue_clerk.getToken) {
                return await window.__vue_clerk.getToken();
            }
            if (window.Clerk && window.Clerk.session) {
                return await window.Clerk.session.getToken();
            }
            if (window.Clerk && window.Clerk.getToken) {
                return await window.Clerk.getToken();
            }
        } catch (e) {
            console.warn('Could not get Clerk token:', e.message);
        }
        return null;
    },

    /**
     * Get the Authorization header value: APPID:AccessToken
     */
    _getAuthHeader() {
        const appId = this.appId || localStorage.getItem('fyers_app_id');
        const token = this.accessToken || localStorage.getItem('fyers_access_token');
        return appId + ':' + token;
    },

    /**
     * Check if user has a valid Fyers session.
     */
    isAuthenticated() {
        return !!(this.accessToken || localStorage.getItem('fyers_access_token'));
    },

    /**
     * Load saved session from localStorage.
     */
    loadSavedSession() {
        this.accessToken = localStorage.getItem('fyers_access_token');
        this.appId = localStorage.getItem('fyers_app_id');
        return this.accessToken;
    },

    /**
     * Clear session and remove stored credentials.
     */
    clearSession() {
        this.accessToken = null;
        this.appId = null;
        this._pendingState = null;
        localStorage.removeItem('fyers_access_token');
        localStorage.removeItem('fyers_app_id');
        this.closeWebSocket();
    },

    /**
     * Fetch user profile to validate the token.
     */
    async getProfile() {
        try {
            const response = await axios.get(FYERS_API_URL + '/profile', {
                headers: { Authorization: this._getAuthHeader() }
            });
            return response.data;
        } catch (error) {
            console.error('Fyers profile error:', error);
            throw error;
        }
    },

    // ─── Symbol Search ───

    /**
     * Search symbols via Fyers indus API through backend proxy.
     * Requires Fyers authentication.
     */
    async searchSymbols(query, limit = 30, type = 'ALLMFIPO') {
        if (!query || query.length < 1) return [];

        try {
            const token = await this._getClerkToken();
            if (!token) return [];

            const response = await axios.get(API_BASE + '/api/fyers/search', {
                params: {
                    query: query.toUpperCase(),
                    limit,
                    type
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                // Cache the results for local filtering
                this.symbols = response.data.symbols || [];
                return this.symbols;
            }
            return [];
        } catch (error) {
            console.error('Fyers search error:', error);
            return [];
        }
    },

    /**
     * Load symbol master through backend proxy to avoid CORS.
     * Kept for backward compatibility - delegates to search.
     */
    async loadSymbolMaster(exchange = 'NSE_CM') {
        // Load a broad search to populate cache
        return this.searchSymbols('A', 100, 'ALLMFIPO');
    },

    // ─── Historical Data ───

    /**
     * Calculate the from-date based on timeframe and API limits.
     * Uses optimal range per timeframe to avoid fetching excessive data:
     * - Seconds: 1-2 days (enough candles for charting)
     * - 1-3 min: 5 days
     * - 5-10 min: 10 days
     * - 15-30 min: 30 days
     * - 1H-4H: 60 days
     * - Daily/Weekly/Monthly: up to 365 days
     */
    _calculateFromDate(timeframe, endTime) {
        const now = Number.isFinite(endTime) ? moment(endTime) : moment();
        const resolution = TIMEFRAME_MAP[timeframe] || '5';

        // Optimal days to fetch per timeframe (balances data vs performance)
        const OPTIMAL_DAYS = {
            '5S': 1,
            '10S': 1,
            '15S': 2,
            '30S': 2,
            '45S': 3,
            '1': 5,
            '2': 7,
            '3': 10,
            '5': 10,
            '10': 15,
            '15': 30,
            '20': 30,
            '30': 45,
            '60': 60,
            '120': 75,
            '240': 90,
            'D': 365
        };

        const maxDays = DATE_RANGE_LIMITS[resolution] || 100;
        const optimalDays = OPTIMAL_DAYS[resolution] || maxDays;
        // Never exceed API limit
        const safeDays = Math.min(optimalDays, maxDays);

        const fromDate = now.clone().subtract(safeDays, 'days');


        return fromDate.format('YYYY-MM-DD');
    },

    /**
     * Aggregate daily candles into weekly or monthly candles.
     * @param {Array} candles - Array of [timestamp, open, high, low, close, volume]
     * @param {String} timeframe - '1W' for weekly or '1M' for monthly
     * @returns {Array} Aggregated candles
     */
    _aggregateCandles(candles, timeframe) {
        if (!candles || candles.length === 0) return [];
        
        const aggregated = [];
        let currentGroup = [];
        let currentPeriod = null;
        
        for (const candle of candles) {
            const timestamp = candle[0];
            const date = moment(timestamp);
            let periodKey;
            
            if (timeframe === '1W') {
                // Use ISO week (Monday-Sunday)
                periodKey = date.format('YYYY-WW');
            } else if (timeframe === '1M') {
                // Use year-month
                periodKey = date.format('YYYY-MM');
            }
            
            if (periodKey !== currentPeriod) {
                // Save previous group if exists
                if (currentGroup.length > 0) {
                    aggregated.push(this._combineCandles(currentGroup, timeframe));
                }
                // Start new group
                currentGroup = [candle];
                currentPeriod = periodKey;
            } else {
                currentGroup.push(candle);
            }
        }
        
        // Don't forget the last group
        if (currentGroup.length > 0) {
            aggregated.push(this._combineCandles(currentGroup, timeframe));
        }
        
        return aggregated;
    },

    /**
     * Combine a group of candles into a single candle.
     * @param {Array} group - Array of candles to combine
     * @param {String} timeframe - Timeframe for logging
     * @returns {Array} Combined candle [timestamp, open, high, low, close, volume]
     */
    _combineCandles(group, timeframe) {
        if (group.length === 0) return null;
        if (group.length === 1) return group[0];
        
        const firstCandle = group[0];
        const lastCandle = group[group.length - 1];
        
        let high = firstCandle[2];
        let low = firstCandle[3];
        let volume = 0;
        
        for (const candle of group) {
            high = Math.max(high, candle[2]);
            low = Math.min(low, candle[3]);
            volume += candle[5];
        }
        
        return [
            firstCandle[0],  // Timestamp of first candle (period start)
            firstCandle[1],  // Open of first candle
            high,            // Highest high in period
            low,             // Lowest low in period
            lastCandle[4],   // Close of last candle
            volume           // Sum of volumes
        ];
    },

    /**
     * Validate date range against API limits.
     * Returns true if the range is valid, false otherwise.
     */
    _validateDateRange(fromDate, toDate, resolution) {
        const maxDays = DATE_RANGE_LIMITS[resolution] || 100;
        const from = moment(fromDate);
        const to = moment(toDate);
        const daysDiff = to.diff(from, 'days');
        
        if (daysDiff > maxDays) {
            console.warn(`[Fyers] Date range exceeds API limit for ${resolution}: ${daysDiff} days requested, max ${maxDays} days allowed`);
            return false;
        }
        
        return true;
    },

    /**
     * Load historical candle data through backend proxy to avoid CORS.
     * Returns array of [timestamp, open, high, low, close, volume].
     * Includes automatic retry with backoff on rate-limit (429) errors.
     * Enforces Fyers API date range limits.
     */
    async loadHistoricalData(symbol, timeframe, _retryCount, endTime) {
        const retryCount = _retryCount || 0;

        if (!this.isAuthenticated()) {
            throw new Error('User not authenticated with Fyers');
        }

        // Validate symbol before proceeding
        if (!symbol || typeof symbol !== 'string' || symbol.trim() === '') {
            console.error('[Fyers] loadHistoricalData called with invalid symbol:', symbol);
            throw new Error('Symbol is required but not provided');
        }

        // Validate timeframe
        if (!timeframe || !TIMEFRAME_MAP[timeframe]) {
            console.error('[Fyers] Invalid timeframe:', timeframe);
            throw new Error(`Invalid timeframe: ${timeframe}. Supported: ${Object.keys(TIMEFRAME_MAP).join(', ')}`);
        }

        const resolution = TIMEFRAME_MAP[timeframe];
        const now = Number.isFinite(endTime) ? moment(endTime) : moment();
        const rangeTo = now.format('YYYY-MM-DD');
        const rangeFrom = this._calculateFromDate(timeframe, endTime);
        const fySymbol = this._normalizeFyersSymbol(symbol.trim());
        
        // Validate date range against API limits
        if (!this._validateDateRange(rangeFrom, rangeTo, resolution)) {
            const maxDays = DATE_RANGE_LIMITS[resolution] || 100;
            throw new Error(`Date range exceeds API limit for ${timeframe}. Maximum ${maxDays} days allowed.`);
        }
        

        if (!fySymbol || fySymbol.trim() === '') {
            throw new Error('Symbol normalization failed for: ' + symbol);
        }

        // Validate date range
        if (moment(rangeFrom).isAfter(now)) {
            throw new Error(`Invalid date range: range_from (${rangeFrom}) cannot be after current date (${rangeTo})`);
        }

        try {
            const token = await this._getClerkToken();
            const response = await axios.get(API_BASE + '/api/fyers/history', {
                params: {
                    symbol: fySymbol,
                    resolution: resolution,
                    date_format: 1,
                    range_from: rangeFrom,
                    range_to: rangeTo,
                    cont_flag: 1
                },
                headers: { Authorization: `Bearer ${token}` },
                timeout: 15000
            });

            if (response.data && response.data.success && response.data.candles) {
                // Validate that candles data exists and is an array
                if (!Array.isArray(response.data.candles) || response.data.candles.length === 0) {
                    console.warn('[Fyers] No candle data returned for symbol:', fySymbol, 'timeframe:', timeframe);
                    return [];
                }
                
                // Fyers returns: [epoch_seconds, open, high, low, close, volume]
                const processedCandles = response.data.candles.map(candle => [
                    candle[0] * 1000,  // epoch seconds → ms
                    parseFloat(candle[1]),
                    parseFloat(candle[2]),
                    parseFloat(candle[3]),
                    parseFloat(candle[4]),
                    parseFloat(candle[5])
                ]).filter(candle => {
                    // Filter out invalid candles
                    return candle && candle.length === 6 && 
                           !isNaN(candle[1]) && !isNaN(candle[2]) && 
                           !isNaN(candle[3]) && !isNaN(candle[4]) && 
                           candle[1] > 0 && candle[2] > 0 && candle[3] > 0 && candle[4] > 0;
                });
                
                
                // Aggregate data for weekly/monthly timeframes
                if (timeframe === '1W' || timeframe === '1M') {
                    return this._aggregateCandles(processedCandles, timeframe);
                }
                
                return processedCandles;
            } else {
                const errMsg = (response.data && response.data.message) || 'Failed to load historical data';
                const errorDetails = response.data ? JSON.stringify(response.data) : 'No response data';
                console.error('[Fyers] history error:', errMsg, 'Details:', errorDetails);
                
                // Provide more specific error messages
                if (response.data && response.data.error_code) {
                    throw new Error(`Fyers API Error ${response.data.error_code}: ${errMsg}`);
                }
                throw new Error(errMsg);
            }
        } catch (error) {
            // Handle auth expiry — clear session so user can re-authenticate
            const status = error.response && error.response.status;
            const authExpired = error.response && error.response.data && error.response.data.authExpired;
            if (status === 401 && authExpired) {
                console.error('[Fyers] Access token expired, clearing session');
                this.clearSession();
                throw new Error('Fyers session expired. Please reconnect your Fyers account.');
            }

            // Retry on rate-limit (429) or server errors (5xx) up to 2 times
            if (retryCount < 2 && (status === 429 || (status >= 500 && status < 600))) {
                const backoff = (retryCount + 1) * 2000; // 2s, 4s
                console.warn(`[Fyers] Rate limited or server error (${status}) for ${fySymbol} tf=${timeframe}, retrying in ${backoff}ms...`);
                await new Promise(r => setTimeout(r, backoff));
                return this.loadHistoricalData(symbol, timeframe, retryCount + 1, endTime);
            }
            console.error('[Fyers] loadHistoricalData error:', error.message);
                throw error;
        }
    },

    /**
     * Get real-time quotes for symbols.
     */
    async getQuotes(symbols) {
        if (!this.isAuthenticated()) {
            throw new Error('User not authenticated with Fyers');
        }

        const symbolList = Array.isArray(symbols) ? symbols.join(',') : symbols;

        try {
            const response = await axios.get(FYERS_DATA_URL + '/quotes', {
                params: { symbols: symbolList },
                headers: { Authorization: this._getAuthHeader() }
            });
            return response.data;
        } catch (error) {
            console.error('Fyers quotes error:', error);
            throw error;
        }
    },

    // ─── WebSocket (Real-time Data) ───

    /**
     * Create and connect the Fyers data WebSocket.
     * Gets WebSocket URL from server (which has the access token).
     */
    async connectWebSocket(symbols, onMessage) {
        if (!this.accessToken) {
            console.error('[Fyers] Cannot connect WebSocket: not authenticated');
            return null;
        }

        // Close existing connection
        this.closeWebSocket();

        try {
            // Get the access token in appId:accessToken format (required by Fyers WS)
            const token = this.accessToken || localStorage.getItem('fyers_access_token');
            const appId = this.appId || localStorage.getItem('fyers_app_id');
            
            if (!token) {
                console.error('[Fyers] Missing access token for WebSocket');
                return null;
            }

            // Fyers WebSocket requires appId:accessToken format
            const authToken = appId ? (appId + ':' + token) : token;

            // Fyers data WebSocket endpoint with access_token
            const wsUrl = FYERS_WS_URL + `?access_token=${authToken}`;

            
            // Store symbols and callback for reconnection
            this._wsSymbols = symbols;
            this._wsOnMessage = onMessage;
            
            this.ws = new WebSocket(wsUrl);

            this.ws.onopen = () => {
                this.reconnectAttempts = 0;
                
                // Subscribe to symbols immediately after connection
                if (symbols && Array.isArray(symbols)) {
                    this._subscribeSymbols(symbols);
                }
            };

            this.ws.onmessage = (event) => {
                try {
                    let data;
                    
                    // Handle different message formats
                    if (typeof event.data === 'string') {
                        data = JSON.parse(event.data);
                    } else if (event.data instanceof ArrayBuffer) {
                        data = this._parseBinaryMessage(event.data);
                    } else if (event.data instanceof Blob) {
                        const reader = new FileReader();
                        reader.onload = () => {
                            try {
                                const parsed = this._parseBinaryMessage(reader.result);
                                if (onMessage && parsed) onMessage(parsed);
                            } catch (e) {
                                console.error('Error parsing Fyers WS blob:', e);
                            }
                        };
                        reader.readAsArrayBuffer(event.data);
                        return;
                    }
                    
                    // Skip empty/null data
                    
                    if (onMessage && data) onMessage(data);
                } catch (error) {
                    console.error('Error processing Fyers WS message:', error);
                }
            };

            this.ws.onerror = (error) => {
                console.error('[Fyers] WebSocket error:', error);
                console.error('[Fyers] WebSocket state:', this.ws ? this.ws.readyState : 'no ws instance');
            };

            this.ws.onclose = (event) => {
                
                // Don't reconnect if this was a manual close, auth failed, or server rejected
                // Close codes: 1000=normal, 1008=policy violation, 4001-4099=auth errors
                if (event.code === 1000 || event.code === 1008 || 
                    (event.code >= 4000 && event.code < 4100) ||
                    !this.isAuthenticated()) {
                    return;
                }
                
                // Don't reconnect if connection was rejected immediately (within 2 seconds)
                // This prevents infinite loops when token is invalid
                if (this.reconnectAttempts >= 3) {
                    console.error('[Fyers] Max reconnection attempts reached - stopping');
                    return;
                }
                
                // Exponential backoff reconnection logic
                const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts), 30000);
                this.reconnectAttempts++;
                
                setTimeout(() => {
                    if (this.isAuthenticated()) {
                        this.connectWebSocket(this._wsSymbols, this._wsOnMessage);
                    } else {
                    }
                }, delay);
            };

            return this.ws;
        } catch (error) {
            console.error('Error creating Fyers WebSocket:', error);
            throw error;
        }
    },

    /**
     * Parse binary WebSocket message from Fyers.
     * Fyers v3 data WebSocket returns JSON dict responses.
     * Falls back to binary tick parsing for legacy compatibility.
     */
    _parseBinaryMessage(buffer) {
        try {
            const decoder = new TextDecoder('utf-8');
            const text = decoder.decode(buffer);
            
            // Try to parse as JSON first (Fyers v3 format)
            try {
                return JSON.parse(text);
            } catch (e) {
                // If not JSON, try to parse as binary tick data (legacy)
                if (buffer.byteLength >= 48) {
                    const view = new DataView(buffer);
                    return {
                        type: 'tick',
                        symbol: '',
                        ltp: view.getFloat64(8, true),
                        open_price: view.getFloat64(16, true),
                        high_price: view.getFloat64(24, true),
                        low_price: view.getFloat64(32, true),
                        close_price: view.getFloat64(40, true),
                        vol_traded_today: view.getInt32(48, true),
                        last_traded_time: view.getInt32(52, true)
                    };
                }
            }
        } catch (error) {
            console.error('Error parsing Fyers binary message:', error);
        }
        return null;
    },

    /**
     * Subscribe to real-time data for multiple symbols at once.
     * Uses the Fyers v3 subscription message format.
     */
    _subscribeSymbols(symbols) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[Fyers] WebSocket not connected, cannot subscribe');
            return;
        }

        const fySymbols = symbols.map(s => this._normalizeFyersSymbol(s)).filter(Boolean);
        if (fySymbols.length === 0) return;

        // Fyers v3 subscription message format
        const subscribeMsg = {
            symbol: fySymbols,
            type: 'symbolUpdate'
        };

        try {
            this.ws.send(JSON.stringify(subscribeMsg));
        } catch (error) {
            console.error('[Fyers] Failed to send subscription message:', error);
        }
    },

    /**
     * Subscribe to real-time data for a symbol.
     * Uses the Fyers v3 subscription message format.
     */
    subscribeToSymbol(symbol) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[Fyers] WebSocket not connected, cannot subscribe to:', symbol);
            return;
        }

        const fySymbol = this._normalizeFyersSymbol(symbol);
        if (!fySymbol) return;

        // Fyers v3 subscription message format
        const subscribeMsg = {
            symbol: [fySymbol],
            type: 'symbolUpdate'
        };

        try {
            this.ws.send(JSON.stringify(subscribeMsg));
        } catch (error) {
            console.error('[Fyers] Failed to send subscription message:', error);
        }
    },

    /**
     * Unsubscribe from real-time data for a symbol.
     * Uses the Fyers v3 unsubscription message format.
     */
    unsubscribeFromSymbol(symbol) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[Fyers] WebSocket not connected, cannot unsubscribe from:', symbol);
            return;
        }

        const fySymbol = this._normalizeFyersSymbol(symbol);
        if (!fySymbol) return;

        // Fyers v3 unsubscription message format
        const unsubscribeMsg = {
            symbol: [fySymbol],
            type: 'unsubscribe'
        };

        try {
            this.ws.send(JSON.stringify(unsubscribeMsg));
        } catch (error) {
            console.error('[Fyers] Failed to send unsubscription message:', error);
        }
    },

    /**
     * Close WebSocket connection properly.
     */
    closeWebSocket() {
        if (this.ws) {
            this.ws.onclose = null; // Remove the onclose handler to prevent reconnection
            this.ws.close(1000, 'Manual close'); // Close with normal code
            this.ws = null;
        }
        this.reconnectAttempts = 0;
        this._wsSymbols = null;
    },

    destroyWs() {
        this.closeWebSocket();
    },

    /**
     * Alias for connectWebSocket for backward compatibility.
     * Chart.vue calls createWebSocket with (onMessage, onError).
     * Accepts an optional symbols array as the third argument.
     */
    createWebSocket(onMessage, onError, symbols) {
        // Store the error callback
        this._wsOnError = onError;
        // Connect with symbols (may be null — symbols can be subscribed later)
        return this.connectWebSocket(symbols || null, onMessage);
    },

    // ─── Utility ───

    /**
     * Normalize a symbol to Fyers format.
     * Fyers uses: NSE:SBIN-EQ, NSE:NIFTY50-INDEX, NSE:SBIN26AUGFUT, NSE:NIFTY26JUN25000CE
     * F&O symbols do NOT have a suffix like -EQ or -FUT — they are bare (e.g., SBIN26AUGFUT).
     */
    _normalizeFyersSymbol(symbol) {
        if (!symbol) return '';

        // Already in Fyers format with proper suffix (-EQ, -INDEX, -FUT)
        if (symbol.includes(':') && (symbol.includes('-EQ') || symbol.includes('-INDEX') || symbol.includes('-FUT'))) {
            return symbol;
        }

        // Has exchange prefix
        if (symbol.includes(':')) {
            const parts = symbol.split(':');
            const exch = parts[0];
            const sym = parts[1];

            // If it already has a dash suffix, return as-is
            if (sym.includes('-')) {
                return symbol;
            }

            // F&O symbols: contain FUT, CE, PE patterns — do NOT append -EQ
            if (/FUT|CE$|PE$|\d{2}(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)/.test(sym)) {
                return symbol;
            }

            // Index symbols (e.g., NIFTY50, NIFTYBANK, SENSEX)
            if (/^(NIFTY|SENSEX|BANKEX)/.test(sym) && !sym.match(/\d{2}(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)/)) {
                return exch + ':' + sym + '-INDEX';
            }

            // Default: equity
            return exch + ':' + sym + '-EQ';
        }

        // Plain symbol — assume NSE equity
        return 'NSE:' + symbol.toUpperCase() + '-EQ';
    },

    /**
     * Convert Fyers symbol to display format.
     */
    getDisplaySymbol(fySymbol) {
        if (!fySymbol) return '';
        const parts = fySymbol.split(':');
        const sym = parts.length > 1 ? parts[1] : parts[0];
        return sym.replace('-EQ', '').replace('-INDEX', '').replace('-FUT', '');
    },

    /**
     * Check if Fyers authentication is completed (for Tauri polling)
     * Returns authentication status from server
     */
    async checkAuthStatus() {
        try {
            const token = await this._getClerkToken();
            if (!token) return { success: false };

            const response = await axios.get(API_BASE + '/api/fyers/session', {
                headers: { Authorization: 'Bearer ' + token }
            });

            if (response.data && response.data.connected && response.data.accessToken) {
                this.accessToken = response.data.accessToken;
                this.appId = response.data.appId;

                localStorage.setItem('fyers_access_token', this.accessToken);
                localStorage.setItem('fyers_app_id', this.appId);

                return { success: true, accessToken: this.accessToken };
            }

            return { success: false };
        } catch (error) {
            return { success: false };
        }
    }
};
